import { createAsyncThunk } from '@reduxjs/toolkit';
import focusBearApi from 'services/axios-config';
import {
  updateAreProjectsLoading,
  updateAreExternalStatusesSyncing,
  updateIsLoadingProjectTasks,
  updateIsProjectSynching,
  updateIsSynchingProjectTasks,
  updateShowPlatformProjects
} from './slice';
import { HTTP_STATS_CODE } from 'constants/general';
import { toast } from 'react-toastify';
import Endpoints from 'constants/endpoints';
import {
  ProjectTasksPayload,
  SyncProjectExternalStatusPayload,
  SyncProjectsPayload
} from 'interfaces';
import { t } from 'services/i18n.service';
import { captureException } from 'services/posthog.service';
import { refreshTodosAllStatuses } from 'store/reducer/to-do/extra';
import { Project } from 'interfaces/common.interface';

const SYNC_POLL_INTERVAL_MS = 3000;
const SYNC_POLL_MAX_ATTEMPTS = 10;

export const getProjects = createAsyncThunk(
  'platform_integration/get_projects',
  async (platform: string, { dispatch }) => {
    try {
      dispatch(updateAreProjectsLoading(true));
      const { data } = await focusBearApi.get(
        Endpoints.USER_PROJECTS(platform.toLowerCase())
      );
      return data ?? [];
    } catch (error) {
      dispatch(updateShowPlatformProjects(true));
      dispatch(updateAreProjectsLoading(false));
      captureException(error);
    }
  }
);

export const syncProjects = createAsyncThunk(
  'platform_integration/sync_projects',
  async (
    {
      portal_id,
      project_id,
      platform,
      are_multiple_projects_being_synced,
      only_assigned
    }: SyncProjectsPayload,
    { dispatch }
  ) => {
    try {
      !are_multiple_projects_being_synced &&
        dispatch(updateIsProjectSynching(true));
      const { status } = await focusBearApi.post(
        `${Endpoints.SYNC_PROJECT(platform.toLowerCase())}`,
        { only_assigned },
        {
          params: {
            portal_id,
            project_id
          }
        }
      );
      if (!are_multiple_projects_being_synced) {
        dispatch(updateIsProjectSynching(false));
        status === HTTP_STATS_CODE.CREATED && dispatch(getProjects(platform));
      }

      return project_id;
    } catch (error) {
      dispatch(updateIsProjectSynching(false));
      captureException(error);
      return null;
    }
  }
);

export const syncProjectExternalStatus = createAsyncThunk(
  'to_do/sync_project_external_status',
  async (
    {
      project_id,
      external_statuses,
      is_apply_to_all_projects
    }: SyncProjectExternalStatusPayload,
    { dispatch }
  ) => {
    try {
      !is_apply_to_all_projects &&
        dispatch(updateAreExternalStatusesSyncing(true));
      const { status } = await focusBearApi.put(
        Endpoints.SYNCED_PROJECTS_EXTERNAL_STATUS,
        {
          project_id,
          external_statuses
        }
      );
      !is_apply_to_all_projects &&
        dispatch(updateAreExternalStatusesSyncing(false));
      status !== HTTP_STATS_CODE.SUCCESS &&
        toast.error(t('to_do_procrastinate.could_not_update_statuses'));
    } catch (error) {
      !is_apply_to_all_projects &&
        dispatch(updateAreExternalStatusesSyncing(false));
      captureException(error);
      return null;
    }
  }
);

export const syncProjectTasks = createAsyncThunk(
  'platform_integration/sync_project_tasks',
  async (
    { platform, portalId, projectId }: ProjectTasksPayload,
    { dispatch }
  ) => {
    try {
      dispatch(updateIsSynchingProjectTasks(true));

      // Capture the server-side synced_at before triggering sync to avoid clock skew issues
      const { data: projectsBefore } = await focusBearApi.get(
        Endpoints.USER_PROJECTS(platform.toLowerCase())
      );
      const syncedAtBefore = (projectsBefore as Project[])?.find(
        (p) => p.project_id === projectId
      )?.synced_at;

      const { status } = await focusBearApi.post(
        Endpoints.SYNC_TASKS(platform.toLowerCase())
      );
      if (status !== HTTP_STATS_CODE.CREATED) {
        dispatch(updateIsSynchingProjectTasks(false));
        toast.error(t('could_not_process_the_request'));
        return;
      }

      // Poll until the backend worker updates synced_at beyond the pre-sync snapshot
      let syncCompleted = false;
      for (let attempt = 0; attempt < SYNC_POLL_MAX_ATTEMPTS; attempt++) {
        await new Promise((resolve) =>
          setTimeout(resolve, SYNC_POLL_INTERVAL_MS)
        );
        const { data: projects } = await focusBearApi.get(
          Endpoints.USER_PROJECTS(platform.toLowerCase())
        );
        const syncedAfterStart = (projects as Project[])?.some(
          (p) =>
            p.synced_at &&
            (!syncedAtBefore || p.synced_at > syncedAtBefore)
        );
        if (syncedAfterStart) {
          dispatch(refreshTodosAllStatuses());
          dispatch(getProjects(platform));
          dispatch(getProjectTasks({ platform, portalId, projectId }));
          syncCompleted = true;
          break;
        }
      }

      if (!syncCompleted) {
        toast.error(t('could_not_process_the_request'));
      }
      dispatch(updateIsSynchingProjectTasks(false));
    } catch (error) {
      dispatch(updateIsSynchingProjectTasks(false));
      captureException(error);
    }
  }
);

export const getProjectTasks = createAsyncThunk(
  'platform_integration/get_project_tasks',
  async (
    { platform, portalId, projectId }: ProjectTasksPayload,
    { dispatch }
  ) => {
    try {
      dispatch(updateIsLoadingProjectTasks(true));
      const { data } = await focusBearApi.get(
        Endpoints.GET_TASKS(platform.toLowerCase(), portalId, projectId)
      );
      return data ?? [];
    } catch (error) {
      dispatch(updateIsLoadingProjectTasks(false));
      captureException(error);
    }
  }
);
